import React from 'react';
import { Film, Trophy, Vote, Upload, ArrowRight, Play } from 'lucide-react';
import leaderboardPage from "../assets/leaderboard.png";
import { useNavigate } from 'react-router-dom';

export default function LandingPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen text-white">

      {/* Hero Section */}
      <section className="container mx-auto px-6 py-20 text-center">
        <h2 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-yellow-300 via-orange-300 to-pink-300 bg-clip-text text-transparent">
          Discover Your #1 Movie
        </h2>
        <p className="text-xl md:text-2xl text-purple-200 mb-4 max-w-3xl mx-auto">
          Stop debating. Start voting.
        </p>
        <p className="text-lg text-purple-300 mb-12 max-w-2xl mx-auto">
          A fun, addictive way to figure out your all-time favorite films through head-to-head matchups powered by ELO rankings.
        </p>
        <button 
        onClick={() => navigate("/auth/signup")}
        className="px-8 py-4 bg-gradient-to-r from-orange-400 to-pink-500 hover:from-orange-500 hover:to-pink-600 rounded-lg text-lg font-semibold flex items-center gap-2 mx-auto transition-all transform hover:scale-105">
          Get Started Free
          <ArrowRight className="w-5 h-5" />
        </button>
        <p className="text-sm text-purple-300 mt-4">No ads. No cost. Just movies.</p>
      </section>

      {/* How It Works */}
      <section className="container mx-auto px-6 py-16">
        <h3 className="text-4xl font-bold text-center mb-16">How It Works</h3>
        
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {/* Step 1 */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 hover:bg-white/15 transition-all">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-purple-500 rounded-xl flex items-center justify-center mb-6 mx-auto">
              <Upload className="w-8 h-8" />
            </div>
            <h4 className="text-2xl font-bold mb-4 text-center">1. Import Your Movies</h4>
            <p className="text-purple-200 text-center">
              Start with TMDB's Top 100, or upload your Letterboxd profile to import all the movies you've watched.
            </p>
          </div>

          {/* Step 2 */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 hover:bg-white/15 transition-all">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-pink-500 rounded-xl flex items-center justify-center mb-6 mx-auto">
              <Vote className="w-8 h-8" />
            </div>
            <h4 className="text-2xl font-bold mb-4 text-center">2. Vote on Matchups</h4>
            <p className="text-purple-200 text-center">
              Choose your favorite between two movies. The more you vote, the more accurate your rankings become.
            </p>
          </div>

          {/* Step 3 */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 hover:bg-white/15 transition-all">
            <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-teal-500 rounded-xl flex items-center justify-center mb-6 mx-auto">
              <Trophy className="w-8 h-8" />
            </div>
            <h4 className="text-2xl font-bold mb-4 text-center">3. See Your Rankings</h4>
            <p className="text-purple-200 text-center">
              Watch your personal leaderboard evolve. Filter by genre to find your favorite in each category.
            </p>
          </div>
        </div>
      </section>

      {/* Visual Demo Section */}
      <section className="container mx-auto px-6 py-16">
        <h3 className="text-4xl font-bold text-center mb-12">See It In Action</h3>
        
        <div className="max-w-6xl mx-auto">
          {/* Voting Interface Preview */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 mb-8">
            <h4 className="text-2xl font-bold mb-6 text-center text-yellow-300">The Voting Experience</h4>
            {/* put video of voting page here */}
            <video autoPlay loop controls={false} ><source src="/votingpage.mp4" type="video/mp4"></source></video>
          </div>

          {/* Leaderboard Preview */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8">
            <h4 className="text-2xl font-bold mb-6 text-center text-yellow-300">Your Personal Rankings</h4>
            {/* put image of leaderboard page here */}
            <img alt="screenshot of leaderboard page" src={leaderboardPage}/>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="container mx-auto px-6 py-16">
        <h3 className="text-4xl font-bold text-center mb-12">Why You'll Love It</h3>
        
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-6">
            <h4 className="text-xl font-bold mb-2">🎯 ELO-Powered Rankings</h4>
            <p className="text-purple-200">The same algorithm used in chess rankings ensures accurate results based on your votes.</p>
          </div>
          
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-6">
            <h4 className="text-xl font-bold mb-2">🎬 Genre Filtering</h4>
            <p className="text-purple-200">Discover your #1 movie overall, or find your favorite by genre—horror, comedy, sci-fi, and more.</p>
          </div>
          
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-6">
            <h4 className="text-xl font-bold mb-2">📊 Letterboxd Integration</h4>
            <p className="text-purple-200">Already track movies on Letterboxd? Import your entire watchlist instantly.</p>
          </div>
          
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-6">
            <h4 className="text-xl font-bold mb-2">🆓 Completely Free</h4>
            <p className="text-purple-200">No subscriptions, no ads, no hidden costs. This is a passion project for movie lovers.</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-6 py-20 text-center">
        <div className="max-w-3xl mx-auto bg-gradient-to-r from-purple-600/30 to-pink-600/30 backdrop-blur-md rounded-2xl p-12 border border-white/20">
          <h3 className="text-4xl font-bold mb-4">Ready to Find Your #1?</h3>
          <p className="text-xl text-purple-200 mb-8">
            Join movie enthusiasts discovering their all-time favorites through the power of choice.
          </p>
          <button 
            onClick={() => navigate("/auth/signup")}
            className="px-10 py-4 bg-gradient-to-r from-orange-400 to-pink-500 hover:from-orange-500 hover:to-pink-600 rounded-lg text-lg font-semibold flex items-center gap-2 mx-auto transition-all transform hover:scale-105">
            Start Voting Now
            <ArrowRight className="w-5 h-5" />
          </button>
          <p className="text-sm text-purple-300 mt-4">Takes less than a minute to get started</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="container mx-auto px-6 py-8 border-t border-white/10">
        <div className="text-center text-purple-300">
          <p>Movie Rater • A free hobby project for movie lovers</p>
          <p className="text-sm mt-2">Made for the ❤️ of cinema</p>
        </div>
      </footer>
    </div>
  );
}